
import React, { useCallback, useEffect, useState } from "react";
import { Box, useMediaQuery, useTheme, Typography, Divider } from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TuneIcon from "@mui/icons-material/Tune";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { COLORS } from "../../assets/theme";
import CustomDateRangePicker from "../../components/common/CustomDateRangePicker";
import SearchForm from "../../components/common/SearchForm";
import config from "../../config.json";
import {
  DateRange,
  IField,
  Option,
  Params,
} from "../../types/table/interfaces.type";
import {
  loadCountriesOptions,
  loadOperatorsByCountry,
  pageAsOptions,
} from "../../utils/commonDataUtils";
import {
  CdrStatus,
  IMonitor,
  useMonitorQuery,
} from "../../services/LiveMonitorService";
import { fetchClients } from "../../services/ClientService";
import DateFilterButtons from "../../components/DateFilterButtons";
import { DlrStatus } from "../../services/MessageReSubmissionService";
import {
  fetchProfiles,
  useProfilesQuery,
} from "../../services/ProfilesService";
import LiveMonitorTable from "../../components/LiveMonitorTable";
import ConditionValuesField from "../../components/ConditionValuesField";
import { fetchCurrency } from "../../services/CurrencyService";
import { IAutoCompleteProps } from "../../components/common/AutoComplete";
import { fetchProviders } from "../../services/ProviderService";
import CustomDialog from "../../components/common/CustomDialog";
import CustomTextField from "../../components/common/CustomTextField";
import CustomButton from "../../components/common/CustomButton";
import ToastNotification from "../../components/ToastNotification";
import {
  repriceCdrs,
  IRepricingSession,
  RepricingSessionStatus,
  useRepricingSessionsQuery,
} from "../../services/MessageTrackerService";
import RepricingSessionsTable from "../../components/RepricingSessionsTable";
import { hasJwtPermission } from "../../services/ServicesHelpers";
interface NameOption {
  label: string;
  value: number;
}

/* ── Shared layout helpers (mirrors RepricingSessionsTable design) ── */
const CField: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ flex: "1 1 30%", minWidth: 90 }}>
    <Typography sx={{ color: COLORS.placeHolderColor, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, fontSize: "0.62rem", lineHeight: 1.2, display: "block" }}>
      {label}
    </Typography>
    <Typography sx={{ mt: 0.1, color: COLORS.typographyColor, wordBreak: "break-word", fontSize: "0.8rem" }}>
      {value ?? "—"}
    </Typography>
  </Box>
);

const CFieldRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box display="flex" flexWrap="wrap" rowGap={1} columnGap={0.75}>{children}</Box>
);

const CSection: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ px: 1, py: 1, borderRadius: 1.5, border: `1px solid ${COLORS.dividerColor}`, backgroundColor: COLORS.White, display: "flex", flexDirection: "column", gap: 1 }}>
    {children}
  </Box>
);

const CSectionTitle: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
    <Box sx={{ color: COLORS.GOLD, display: "flex", alignItems: "center" }}>{icon}</Box>
    <Typography sx={{ fontWeight: 700, color: COLORS.typographyColor, letterSpacing: 0.3, fontSize: "0.72rem", textTransform: "uppercase" }}>
      {title}
    </Typography>
  </Box>
);

interface ConfirmContentProps {
  newRate: string;
  newCost: string;
  totalCdrCount: number;
  fromDate: Date | null;
  toDate: Date | null;
  clientOptions: { label: string; value: string | number }[];
  providersOptions: { label: string; value: string | number }[];
  countriesOptions: { label: string; value: string | number }[];
  operatorsByCountry: { label: string; value: string | number }[];
  filters: any;
  appliedFilters: any;
  conditionStatuses: { field?: string; conditionType?: string; value?: string; status?: string }[];
}

const ConfirmRepriceDialogContent: React.FC<ConfirmContentProps> = ({
  newRate, newCost, totalCdrCount,
  fromDate, toDate,
  clientOptions, providersOptions, countriesOptions, operatorsByCountry,
  filters, appliedFilters, conditionStatuses,
}) => {
  const fmt = (d: Date | null) => d ? d.toLocaleString() : "—";
  const findLabel = (opts: { label: string; value: string | number }[], id?: string | number) =>
    opts.find(o => String(o.value) === String(id ?? ""))?.label || (id ? String(id) : "—");

  const activeConditions = (conditionStatuses ?? []).filter(c => c.field && (c.value || c.status));
  const fieldLabelMap: Record<string, string> = {
    originalSource: "SID",
    postTranslatedSource: "Translated SID",
    originalMessageText: "Content",
    messageText: "Translated Content",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {/* Summary banner */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.75, borderRadius: 1.5, backgroundColor: "#fffbeb", border: "1px solid #fff3cd" }}>
        <ScheduleIcon sx={{ color: "#92620a", fontSize: 18 }} />
        <Box flex={1}>
          <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.2 }}>
            Action
          </Typography>
          <Typography sx={{ fontWeight: 700, color: "#92620a", fontSize: "0.85rem", lineHeight: 1.3 }}>
            Confirm Repricing
          </Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ textAlign: "center", px: 0.5 }}>
          <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.2 }}>Total CDRs</Typography>
          <Typography sx={{ fontWeight: 700, color: COLORS.typographyColor, fontSize: "1.1rem", lineHeight: 1.3 }}>{totalCdrCount}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ textAlign: "center", px: 0.5 }}>
          <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.2 }}>New Rate</Typography>
          <Typography sx={{ fontWeight: 700, color: COLORS.typographyColor, fontSize: "0.85rem", lineHeight: 1.3 }}>{newRate || "—"}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box sx={{ textAlign: "center", px: 0.5 }}>
          <Typography sx={{ color: COLORS.placeHolderColor, fontSize: "0.62rem", textTransform: "uppercase", letterSpacing: 0.5, lineHeight: 1.2 }}>New Cost</Typography>
          <Typography sx={{ fontWeight: 700, color: COLORS.typographyColor, fontSize: "0.85rem", lineHeight: 1.3 }}>{newCost || "—"}</Typography>
        </Box>
      </Box>

      {/* Details */}
      <CSection>
        <CSectionTitle icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />} title="Date Range" />
        <CFieldRow>
          <CField label="From Date" value={fmt(fromDate)} />
          <CField label="To Date" value={fmt(toDate)} />
        </CFieldRow>
        <Divider sx={{ my: 0.25 }} />
        <CSectionTitle icon={<TuneIcon sx={{ fontSize: 14 }} />} title="Applied Filters" />
        <CFieldRow>
          <CField label="Customer" value={findLabel(clientOptions, filters.connectionProfileId)} />
          <CField label="Vendor" value={findLabel(providersOptions, filters.vendorId)} />
          <CField label="Country" value={findLabel(countriesOptions, filters.destinationCountryId)} />
          <CField label="Operator" value={findLabel(operatorsByCountry, filters.destinationOperatorId)} />
          <CField label="CDR Status" value={filters.status || "—"} />
          <CField label="DLR Status" value={filters.deliveryStat || "—"} />
          {appliedFilters.search?.trim() && <CField label="Search" value={appliedFilters.search} />}
        </CFieldRow>
        {activeConditions.length > 0 && (
          <>
            <Divider sx={{ my: 0.25 }} />
            <CSectionTitle icon={<TuneIcon sx={{ fontSize: 14 }} />} title="Conditions" />
            <CFieldRow>
              {activeConditions.map((c, i) => (
                <CField
                  key={i}
                  label={fieldLabelMap[c.field ?? ""] ?? c.field ?? ""}
                  value={`${c.conditionType}: ${c.value || c.status}`}
                />
              ))}
            </CFieldRow>
          </>
        )}
      </CSection>
    </Box>
  );
};

const LiveMonitorPage: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [appliedFilters, setAppliedFilters] = useState<
    Params<
      IMonitor &
        DateRange & {
          // profileId: string;
          profileType: string;
          connectionProfileId: string;
          vendorId: string;
          destinationCountryId: string;
          destinationOperatorId: string;
          deliveryStat: string;
          status: string;
          messageText: string;
          terminatorId: string;
          profileIdCheck: boolean;
        }
    >
  >({});

  const [providersOptions, setProvidersOptions] = useState<Option[]>([]);
  const [countriesOptions, setCountriesOptions] = useState<Option[]>([]);
  const [clientOptions, setClientOptions] = useState<Option[]>([]);
  const [operatorsByCountry, setOperatorsByCountry] = useState<Option[]>([]);
  const [isOperatorDisabled, setIsOperatorDisabled] = useState(true);
  const [conditionStatuses, setConditionStatuses] = useState<
    {
      conditionType?: string;
      status?: string;
      field?: string;
      value?: string;
    }[]
  >([{ conditionType: "Equals", status: undefined }]);
  const [filters, setFilters] = useState<
    Params<
      IMonitor & {
        profileId: string;
        profileIdCheck: boolean;
        groupBySenderId: boolean;
        profileType: string;
        connectionProfileId: string;
        vendorId: string;
        destinationCountryId: string;
        destinationOperatorId: string;
        deliveryStat: string;
        status: string;
        messageText: string;
        terminatorId: string;
        search: string;
        clientProfileId?: string;
        vendorProfileId?: string;
      }
    >
  >({
    profileId: "",
    profileType: "CLIENT",
    connectionProfileId: "",
    vendorId: "",
    destinationCountryId: "",
    profileIdCheck: false,
    destinationOperatorId: "",
    deliveryStat: "",
    status: "",
    messageText: "",
    terminatorId: "",
    search: "",
    clientProfileId: "",
    vendorProfileId: "",
  });

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [newRate, setNewRate] = useState<string>("");
  const [newCost, setNewCost] = useState<string>("");
  const [isRepricing, setIsRepricing] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [sessionsDialogOpen, setSessionsDialogOpen] = useState(false);

  const [profileMap, setProfileMap] = useState<Record<string, any>>({});
  const [currencyMap, setCurrencyMap] = useState<Record<string, string>>({});

  const [tableData, setTableData] = useState<IMonitor[]>([]);

  const [isQueryEnabled, setIsQueryEnabled] = useState(false);

  const { data: repricingSessionsData } = useRepricingSessionsQuery({});

  const repricingSessions: IRepricingSession[] =
    repricingSessionsData?.content ?? [];

  const hasRunningSession = repricingSessions.some(
    (s) => s.repricingStatus === RepricingSessionStatus.RUNNING
  );

  const {
    data: monitorData,
    isLoading: isMonitorLoading,
    isFetching: isMonitorFetching,
  } = useMonitorQuery(appliedFilters, isQueryEnabled);

  const [profileOptions, setProfileOptions] = useState<NameOption[]>([]);

  const CdrStatusOptions = Object.entries(CdrStatus)
    .filter(([key, value]) => isNaN(Number(key)))
    .map(([key, value]) => ({
      label: key,
      value: String(key),
    }));

  useEffect(() => {
    const fetchAllProfiles = async () => {
      try {
        const allProfilesPage = await fetchProfiles({ size: 10000 });
        if (allProfilesPage && allProfilesPage.content) {
          const options = allProfilesPage.content.map((profile: any) => ({
            label: profile.name,
            value: profile.id,
          }));
          setProfileOptions(options);
        }
      } catch (error) {
        console.error("Error fetching all profiles for dropdown:", error);
      }
    };
    fetchAllProfiles();
  }, []);

  useEffect(() => {
    const fetchProfilesAndCurrencies = async () => {
      if (!tableData || tableData.length === 0) return;

      const uniqueProfiles = Array.from(
        new Set(tableData.map((row) => row.profile).filter(Boolean))
      );

      const profilesPages = await Promise.all(
        uniqueProfiles.map((name) => fetchProfiles({ name }))
      );
      const profiles = profilesPages
        .map((page) =>
          page.content && page.content.length > 0 ? page.content[0] : null
        )
        .filter(Boolean);

      const newProfileMap: Record<string, any> = {};
      profiles.forEach((profile) => {
        if (profile) {
          newProfileMap[profile.name] = profile;
        }
      });
      setProfileMap(newProfileMap);

      const uniqueCurrencyIds = Array.from(
        new Set(
          profiles
            .map((p) => p && p.currencyId)
            .filter((id): id is number => id !== null && id !== undefined)
        )
      );

      const currencies = await Promise.all(
        uniqueCurrencyIds.map((id) => fetchCurrency(id))
      );

      const newCurrencyMap: Record<string, string> = {};
      currencies.forEach((currency, idx) => {
        const currencyId = uniqueCurrencyIds[idx];
        if (currency && currencyId !== null && currencyId !== undefined) {
          newCurrencyMap[currencyId] = currency.name;
        }
      });
      setCurrencyMap(newCurrencyMap);
    };

    fetchProfilesAndCurrencies();
  }, [tableData]);
  useEffect(() => {
    if (monitorData) {
      setTableData(
        monitorData.map((monitor) => ({
          ...monitor,
          clientId: monitor.id ?? "",
        }))
      );
    }
  }, [monitorData]);

  const dlrStatusOptions = Object.entries(DlrStatus)
    .filter(([key]) => isNaN(Number(key)))
    .map(([key]) => ({
      label: key,
      value: key,
    }));

  const { data: profileOption } = useProfilesQuery();
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const clientPage = await fetchClients({
          size: config.MAX_PAGE_SIZE,
        });
        setClientOptions(pageAsOptions(clientPage));
        const vendorPage = await fetchProviders({
          size: config.MAX_PAGE_SIZE,
        });
        setProvidersOptions(pageAsOptions(vendorPage));

        setCountriesOptions(await loadCountriesOptions());
      } catch (error: any) {
        console.error("Error loading initial data:", error);
      }
    };

    loadInitialData();
  }, [profileOption]);

  const transformConditions = (
    conditions: {
      field?: string;
      conditionType?: string;
      status?: string;
      value?: string;
    }[]
  ): Record<string, string[]> =>
    conditions.reduce((acc, { field, conditionType, status, value }) => {
      if (field && conditionType) {
        const typeCamel = conditionType.replace(/_([a-z])/g, (_, letter) =>
          letter.toUpperCase()
        );
        const key = `${field}${typeCamel}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        if (acc[key].length === 0 && (status === undefined || status === "")) {
          acc[key].push(value ?? "");
        } else {
          acc[key].push(status ?? "");
        }
      }
      return acc;
    }, {} as Record<string, string[]>);

  const logConditionStatuses = (
    statuses?: {
      field?: string;
      conditionType?: string;
      status?: string;
      label?: string;
      value?: string;
    }[]
  ) => {
    if (!statuses) {
      console.log("No condition statuses provided");
      return;
    }

    const result: Record<string, string[]> = {};

    statuses.forEach((condition, idx) => {
      const { field, conditionType, status, value } = condition;
      const typeCamel = conditionType
        ? conditionType.replace(/_([a-z])/g, (_, letter) => letter)
        : "";
      const combinedKey = field && typeCamel ? `${field}${typeCamel}` : "";

      if (!combinedKey) return;

      if (!result[combinedKey]) {
        result[combinedKey] = [];
      }

      if (idx === 0 && (status === undefined || status === "")) {
        result[combinedKey].push(value ?? "");
      } else {
        result[combinedKey].push(status ?? "");
      }
    });
  };

  const handleSearchChange = (search: string) => {
    setAppliedFilters((prev: any) => ({ ...prev, search }));
  };

  const handleSearchClick = () => {
    const conditionParams = transformConditions(conditionStatuses ?? []);
    setAppliedFilters((prev) => ({
      ...filters,
      ...conditionParams,
      status: filters.status,
      search: prev.search ?? "",
      fromDate: fromDate ? fromDate : undefined,
      toDate: toDate ? toDate : undefined,
      _forceRefresh: Date.now(),
    }));
    setIsQueryEnabled(true);
  };
  const handleCountryChange = async (
    value: string | number | (string | number)[]
  ) => {
    const selectedCountry = Array.isArray(value) ? value[0] : value;
    if (selectedCountry) {
      try {
        const destinationCountryId = Number(selectedCountry);
        if (isNaN(destinationCountryId)) throw new Error("Invalid country ID");
        setIsOperatorDisabled(true);
        const ops = await loadOperatorsByCountry(destinationCountryId);
        setOperatorsByCountry(ops);
      } catch (error: any) {
        console.error("Error fetching operators:", error);
        setOperatorsByCountry([]);
      } finally {
        setIsOperatorDisabled(false);
      }
    } else {
      setOperatorsByCountry([]);
      setIsOperatorDisabled(false);
    }
  };

  const autoCompletes: IAutoCompleteProps[] = [
    {
      label: "Profile",
      value: filters.clientProfileId ?? "",
      options: profileOptions,
      onChange: (value: string | number | (string | number)[]) => {
        const selectedProfile = profileOptions.find(
          (option) => option.value === value
        );
        setFilters((prev: any) => ({
          ...prev,
          clientProfileId: selectedProfile
            ? selectedProfile.value.toString()
            : "",
          vendorProfileId: undefined,
        }));
      },
      clearable: true,
    },
    {
      label: "Customer *",
      value: filters.connectionProfileId ?? "",
      options: clientOptions,
      clearable: true,
      placeholder: "Select Client",
      onChange: (value) => {
        const selected = clientOptions.find((option) => option.value === value);
        setFilters((prev: any) => ({
          ...prev,
          connectionProfileId: selected ? String(selected.value) : undefined,
        }));
      },
    },
    {
      label: "Provider",
      value: filters.vendorId ?? "",
      options: providersOptions,
      clearable: true,
      placeholder: "Select Provider",
      onChange: (value) => {
        const selected = providersOptions.find(
          (option) => option.value === value
        );
        setFilters((prev: any) => ({
          ...prev,
          vendorId: selected ? String(selected.value) : undefined,
        }));
      },
    },
    {
      label: "Country *",
      value: filters.destinationCountryId ?? "",
      options: countriesOptions,
      clearable: true,
      placeholder: "Select Country",
      onChange: async (value) => {
        const selected = countriesOptions.find(
          (option) => option.value === value
        );
        setFilters((prev: any) => ({
          ...prev,
          destinationCountryId: selected ? String(selected.value) : "",
          destinationOperatorId: "",
        }));
        if (selected) {
          try {
            setIsOperatorDisabled(true);
            const ops = await loadOperatorsByCountry(Number(selected.value));
            setOperatorsByCountry(ops);
          } catch (error: any) {
            console.error("Error loading operators:", error);
            setOperatorsByCountry([]);
          } finally {
            setIsOperatorDisabled(false);
          }
        }
        try {
          await handleCountryChange(value);
        } catch (error: any) {
          console.error("Error in handleCountryChange:", error);
        }
      },
    },
    {
      label: "Operator *",
      value: filters.destinationOperatorId ?? "",
      options: operatorsByCountry,
      clearable: true,
      disabled: isOperatorDisabled,
      placeholder: "Select Operator",
      onChange: (value) => {
        const selected = operatorsByCountry.find(
          (option) => option.value === value
        );
        setFilters((prev: any) => ({
          ...prev,
          destinationOperatorId: selected ? String(selected.value) : "",
        }));
      },
    },
    {
      label: "Status",
      value: filters.status ?? "",
      options: CdrStatusOptions,
      clearable: true,
      placeholder: "Select Status",
      onChange: (value: string | number | (string | number)[]) => {
        const selectedStatus = Object.keys(CdrStatus).find(
          (key) => key === value
        );
        setFilters((prev) => ({
          ...prev,
          status: selectedStatus || undefined,
        }));
      },
    },
    {
      label: "Delivery Status",
      value: filters.deliveryStat ?? "",
      options: dlrStatusOptions,
      clearable: true,
      placeholder: "Select delivery Status",
      onChange: (value) => {
        const selected = Object.keys(DlrStatus).find((key) => key === value);
        setFilters((prev: any) => ({
          ...prev,
          deliveryStat: selected || "",
        }));
      },
    },
  ];

  // Check if required fields are filled
  const areRequiredFieldsFilled = !!(
    filters.connectionProfileId &&
    filters.destinationCountryId &&
    filters.destinationOperatorId
  );

  const buttons = [
    {
      label: "Search",
      sx: { marginTop: 2 },
      variant: "contained" as const,
      bgColor: COLORS.GOLD,
      onClick: handleSearchClick,
      disabled: !areRequiredFieldsFilled,
      permission: "GET_ALL_MONITORS_REPRICE",
    },
  ];

  const fields: IField<IMonitor>[] = [
    { key: "name", header: "Name" },
    { key: "count", header: "Count" },
    { key: "delivered", header: "Delivered" },
    {
      key: "deliveryPercentage",
      header: "Delivery %",
      transform: async (t: IMonitor) => {
        if (t.count) {
          const percentage = (t.delivered / t.count) * 100;
          return percentage.toFixed(2) + "%";
        }
        return "0%";
      },
    },
    {
      key: "totalCost",
      header: "Total Cost",
      transform: (t: IMonitor) => {
        const value = Number(t.totalCost);
        return isNaN(value) ? "0" : parseFloat(value.toFixed(6)).toString();
      },
    },
    {
      key: "totalRate",
      header: "Total Rate",
      transform: (t: IMonitor) => {
        const value = Number(t.totalRate);
        return isNaN(value) ? "0" : parseFloat(value.toFixed(6)).toString();
      },
    },
    {
      key: "totalProfit",
      header: "Profit",
      transform: (t: IMonitor) => {
        const value = Number(t.totalProfit);
        return isNaN(value) ? "0.000000" : value.toFixed(6);
      },
    },
    {
      key: "profile",
      header: "Currency",
      transform: (t: IMonitor) => {
        if (!t.profile) return "";
        const profile = profileMap[t.profile];
        if (!profile) return "";
        return currencyMap[profile.currencyId] || "";
      },
    },
    { key: "employeeName", header: "Employee" },
  ];

  const getRowCurrency = useCallback(
    (t: IMonitor) => {
      const profile = t.profile ? profileMap[t.profile] : undefined;
      return profile?.currencyId ? currencyMap[profile.currencyId] : undefined;
    },
    [profileMap, currencyMap],
  );

  const handleDateFilterSelect = (option: string) => {
    if (option) {
      const [startStr, endStr] = option.split("_");
      setFromDate(new Date(startStr));
      setToDate(new Date(endStr));
    } else {
      setFromDate(null);
      setToDate(null);
    }
  };

  const handleRepriceClick = () => {
    const rate = parseFloat(newRate);
    const cost = parseFloat(newCost);
    
    if (isNaN(rate) && isNaN(cost)) {
      ToastNotification.error("Please enter valid numbers for rate and cost.");
      return;
    }

    setConfirmDialogOpen(true);
  };

  const handleConfirmReprice = async () => {
    const rate = parseFloat(newRate);
    const cost = parseFloat(newCost);

    setConfirmDialogOpen(false);
    setIsRepricing(true);
    try {
      const conditionParams = transformConditions(conditionStatuses ?? []);
      const repriceParams: any = {
        ...filters,
        ...conditionParams,
        fromDate: fromDate ?? undefined,
        toDate: toDate ?? undefined,
        search: appliedFilters.search ?? undefined,
      };
      
      // Only include newRate if it's a valid number
      if (!isNaN(rate)) {
        repriceParams.newRate = rate;
      }
      // Only include newCost if it's a valid number
      if (!isNaN(cost)) {
        repriceParams.newCost = cost;
      }
      
      const repricedCount = await repriceCdrs(repriceParams);
      ToastNotification.success(`Successfully repriced ${repricedCount} CDRs.`);
      // Clear the inputs after successful reprice
      setNewRate("");
      setNewCost("");
      // Refresh the table after repricing
      handleSearchClick();
    } catch (error: any) {
      ToastNotification.error(error.message || "Repricing failed. Please try again.");
    } finally {
      setIsRepricing(false);
    }
  };

  const handleCancelReprice = () => {
    setConfirmDialogOpen(false);
  };

  // Calculate total CDR count from current table data
  const totalCdrCount = tableData.reduce((sum, row) => sum + (row.count || 0), 0);

  useEffect(() => {
    const today = new Date();
    const yyyy = today.getUTCFullYear();
    const mm = String(today.getUTCMonth() + 1).padStart(2, "0");
    const dd = String(today.getUTCDate()).padStart(2, "0");
    const todayStr = `${yyyy}-${mm}-${dd}`;
    const tomorrow = new Date(today);
    tomorrow.setUTCDate(today.getUTCDate() + 1);
    const yyyy2 = tomorrow.getUTCFullYear();
    const mm2 = String(tomorrow.getUTCMonth() + 1).padStart(2, "0");
    const dd2 = String(tomorrow.getUTCDate()).padStart(2, "0");
    const tomorrowStr = `${yyyy2}-${mm2}-${dd2}`;
    handleDateFilterSelect(`${todayStr}_${tomorrowStr}`);
  }, []);

  return (
    <Box padding={isSmallScreen ? 2 : 3}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <SearchForm
            dateComponent={
              <Box display="flex" flexDirection="column" gap={2} width="100%">
                <Box display="flex" flexDirection="column" gap={2} width="100%">
                  <DateFilterButtons
                    onSelect={handleDateFilterSelect}
                    fromDate={fromDate}
                    toDate={toDate}
                  />
                  <CustomDateRangePicker
                    fromDate={fromDate}
                    toDate={toDate}
                    setFromDate={setFromDate}
                    setToDate={setToDate}
                  />
                </Box>
              </Box>
            }
            autoCompletes={autoCompletes}
            buttons={buttons}
          >
            {conditionStatuses && conditionStatuses.length > 0 && (
              <Box>
                <ConditionValuesField
                  whiteBackground
                  notIfCondition
                  conditionTypeOptions={[
                    { label: "Contains", value: "Contains" },
                    { label: "Equals", value: "Equals" },
                  ]}
                  label="Sender ID"
                  type="text"
                  labelOptions={[
                    { label: "SID", value: "originalSource" },
                    { label: "Translated SID", value: "postTranslatedSource" },
                    { label: "Content", value: "originalMessageText" },
                    { label: "Translated Content", value: "messageText" },
                  ]}
                  onLabelChange={(index, value) => {
                    setConditionStatuses((prev) => {
                      const updatedStatuses = prev.map((c, i) =>
                        i === index ? { ...c, field: value } : c
                      );
                      logConditionStatuses(updatedStatuses);
                      return updatedStatuses;
                    });
                  }}
                  conditionStatuses={conditionStatuses}
                  handleConditionChange={(index, field, status) => {
                    setConditionStatuses((prev) => {
                      const updatedStatuses = prev.map((c, i) =>
                        i === index ? { ...c, [field]: status } : c
                      );
                      logConditionStatuses(updatedStatuses);
                      return updatedStatuses;
                    });
                  }}
                  handleAddCondition={() => {
                    setConditionStatuses((prev) => [
                      ...prev,
                      { conditionType: "", status: "" },
                    ]);
                  }}
                  handleRemoveCondition={(index) => {
                    setConditionStatuses((prev) =>
                      prev.filter((_, i) => i !== index)
                    );
                  }}
                />
              </Box>
            )}
          </SearchForm>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box
          padding={{ xs: 1.5, sm: 3 }}
          borderRadius={2}
          sx={{
            backgroundColor: COLORS.iconColor,
            width: "100%",
          }}
        >
          <Box
            display="flex"
            flexWrap="wrap"
            gap={2}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box
              display="flex"
              flexWrap="wrap"
              gap={2}
              alignItems="flex-end"
              flex="1"
            >
              <Box sx={{ minWidth: 150 }}>
                <CustomTextField
                  label="New Cost"
                  value={newCost}
                  onChange={(e) => setNewCost(e.target.value)}
                  type="number"
                  size="small"
                  bgColor={COLORS.WhiteBackground}
                />
              </Box>
              <Box sx={{ minWidth: 150 }}>
                <CustomTextField
                  label="New Rate"
                  value={newRate}
                  onChange={(e) => setNewRate(e.target.value)}
                  type="number"
                  size="small"
                  bgColor={COLORS.WhiteBackground}
                />
              </Box>
            </Box>
            <Box display="flex" gap={2} alignItems="center">
              <CustomButton
                label="Repricing Sessions"
                onClick={() => setSessionsDialogOpen(true)}
                bgColor={COLORS.GOLD}
                textColor={COLORS.iconColor}
                disabled={!hasJwtPermission("GET_ALL_REPRICING_SESSIONS")}
                variant="contained"
                sx={hasRunningSession ? {
                  background: `linear-gradient(90deg, ${COLORS.GOLD} 0%, #c8a84b 35%, #e8cc78 50%, #c8a84b 65%, ${COLORS.GOLD} 100%)`,
                  backgroundSize: "200% 100%",
                  "@keyframes shimmer-x": {
                    "0%":   { backgroundPosition: "200% center" },
                    "100%": { backgroundPosition: "-200% center" },
                  },
                  animation: "shimmer-x 1.4s linear infinite",
                } : {}}
              />
              <CustomButton
                label={isRepricing ? "Repricing..." : "Reprice"}
                onClick={handleRepriceClick}
                bgColor={COLORS.GOLD}
                textColor={COLORS.iconColor}
                variant="contained"
                disabled={isRepricing || hasRunningSession || (!newRate && !newCost) || !areRequiredFieldsFilled || !hasJwtPermission("REPRICE_CDR")}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <LiveMonitorTable<IMonitor>
          data={tableData}
          filters={appliedFilters}
          fields={fields}
          search={appliedFilters.search}
          setSearch={handleSearchChange}
          profileIdCheck={appliedFilters.profileIdCheck}
          initialPageSize={5}
          isLoading={Boolean(isMonitorLoading || isMonitorFetching)}
          usedBy="repricing"
          getRowCurrency={getRowCurrency}
        />
      </Box>

      <CustomDialog
        open={sessionsDialogOpen}
        onClose={() => setSessionsDialogOpen(false)}
        title="Repricing Sessions"
        mode="history"
        fields={[
          {
            type: "custom",
            component: (
              <Box sx={{ width: "100%" }}>
                <RepricingSessionsTable />
              </Box>
            ),
          },
        ]}
        buttons={[]}
      />

      <CustomDialog
        open={confirmDialogOpen}
        onClose={handleCancelReprice}
        title="Confirm Repricing"
        mode="history"
        dialogSx={{ minWidth: "480px", maxWidth: "480px" }}
        fields={[
          {
            type: "custom",
            component: (
              <ConfirmRepriceDialogContent
                newRate={newRate}
                newCost={newCost}
                totalCdrCount={totalCdrCount}
                fromDate={fromDate}
                toDate={toDate}
                clientOptions={clientOptions}
                providersOptions={providersOptions}
                countriesOptions={countriesOptions}
                operatorsByCountry={operatorsByCountry}
                filters={filters}
                appliedFilters={appliedFilters}
                conditionStatuses={conditionStatuses}
              />
            ),
          },
        ]}
        buttons={[
          {
            label: "Confirm Reprice",
            onClick: handleConfirmReprice,
            bgColor: COLORS.GOLD,
            textColor: COLORS.iconColor,
            variant: "contained",
            disabled: !hasJwtPermission("REPRICE_CDR"),
          },
        ]}
      />
    </Box>
  );
};

export default LiveMonitorPage;
